import {
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { getConfig } from './config';

/**
 * Recruiter sign-in against Cognito.
 *
 * Tokens live in localStorage. That is a deliberate trade-off for an internal
 * hiring tool: it survives a refresh, and the alternative - an httpOnly cookie -
 * would need a same-origin backend the static hosting does not have. If you
 * deploy this to handle candidate data at scale, put the API behind a custom
 * domain that shares an origin with the app and move to cookies.
 */

const STORAGE_KEY = 'ai-interview.session';

/** Fired when a request finds the session is over, so the app can send the recruiter to sign in. */
export const SESSION_ENDED_EVENT = 'ai-interview:session-ended';

/** How long before the ID token expires it is refreshed, so no request leaves with one about to lapse. */
const REFRESH_MARGIN_MS = 5 * 60_000;

export interface Session {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  email: string;
}

function client(): CognitoIdentityProviderClient {
  return new CognitoIdentityProviderClient({ region: getConfig().region });
}

function readSession(): Session | undefined {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
}

/**
 * The stored session, for deciding whether someone is signed in.
 *
 * An ID token that has expired still counts while there is a refresh token to
 * renew it: the next request refreshes it first. Without one, the session is
 * over.
 */
export function loadSession(): Session | undefined {
  const session = readSession();
  if (!session) return undefined;
  if (session.expiresAt - 60_000 < Date.now() && !session.refreshToken) {
    signOut();
    return undefined;
  }
  return session;
}

/**
 * A session whose ID token is good for at least a few more minutes, refreshed
 * first when it is not.
 *
 * Recruiters used to be signed out eight hours after signing in - often half
 * way through writing a role - although Cognito had issued a 30-day refresh
 * token and it had been stored the whole time.
 */
export async function getFreshSession(): Promise<Session | undefined> {
  const session = loadSession();
  if (!session) return undefined;
  if (session.expiresAt - REFRESH_MARGIN_MS > Date.now()) return session;
  return refreshSession();
}

let refreshing: Promise<Session | undefined> | undefined;

/**
 * Exchanges the refresh token for new tokens. Requests that need a refresh at
 * the same moment share one call. Resolves undefined, signed out, when the
 * refresh token has expired or been revoked; a network failure rejects instead,
 * so being offline for a moment does not sign anyone out.
 */
export function refreshSession(): Promise<Session | undefined> {
  // Cleared in `.finally` rather than inside the call, which can settle before
  // this assignment happens and would leave a finished promise cached for good.
  refreshing ??= exchangeRefreshToken().finally(() => {
    refreshing = undefined;
  });
  return refreshing;
}

async function exchangeRefreshToken(): Promise<Session | undefined> {
  const session = readSession();
  if (!session?.refreshToken) {
    signOut();
    return undefined;
  }
  try {
    const response = await client().send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: getConfig().userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: session.refreshToken },
      }),
    );
    // This flow issues no new refresh token; the original stays valid until its own expiry.
    return storeSession({
      ...toSession(response.AuthenticationResult, session.email),
      refreshToken: session.refreshToken,
    });
  } catch (error) {
    if ((error as { name?: string } | undefined)?.name === 'NotAuthorizedException') {
      signOut();
      return undefined;
    }
    throw error;
  }
}

function storeSession(session: Session): Session {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

export function signOut(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export interface NewPasswordRequired {
  kind: 'new_password_required';
  session: string;
  email: string;
}

export type SignInResult = { kind: 'signed_in'; session: Session } | NewPasswordRequired;

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const config = getConfig();
  const response = await client().send(
    new InitiateAuthCommand({
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      ClientId: config.userPoolClientId,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    }),
  );

  // Admin-created users must set their own password before they have a session.
  if (response.ChallengeName === ChallengeNameType.NEW_PASSWORD_REQUIRED) {
    return { kind: 'new_password_required', session: response.Session ?? '', email };
  }

  return { kind: 'signed_in', session: storeSession(toSession(response.AuthenticationResult, email)) };
}

export async function completeNewPassword(
  challenge: NewPasswordRequired,
  newPassword: string,
): Promise<Session> {
  const config = getConfig();
  const response = await client().send(
    new RespondToAuthChallengeCommand({
      ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
      ClientId: config.userPoolClientId,
      Session: challenge.session,
      ChallengeResponses: { USERNAME: challenge.email, NEW_PASSWORD: newPassword },
    }),
  );
  return storeSession(toSession(response.AuthenticationResult, challenge.email));
}

/**
 * Starts a password reset: Cognito emails a code to the account's verified
 * address. The pool is set to hide whether an account exists, so this succeeds
 * the same way for an address with no account - and the page says so.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await client().send(
    new ForgotPasswordCommand({ ClientId: getConfig().userPoolClientId, Username: email }),
  );
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  await client().send(
    new ConfirmForgotPasswordCommand({
      ClientId: getConfig().userPoolClientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    }),
  );
}

/** Cognito's reset errors, in words a recruiter can act on. Anything else passes through. */
export function describeResetError(error: unknown): unknown {
  const name = (error as { name?: string } | undefined)?.name;
  switch (name) {
    case 'CodeMismatchException':
      return new Error('That code is not right. Check the most recent email and try again.');
    case 'ExpiredCodeException':
      return new Error('That code has expired. Send a new one and use it within an hour.');
    case 'InvalidPasswordException':
      return new Error(
        'Choose a password of at least 12 characters with upper and lower case letters and a number.',
      );
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new Error('Too many attempts. Wait a few minutes, then try again.');
    case 'NotAuthorizedException':
    case 'InvalidParameterException':
      // What Cognito says for an account that has never replaced its temporary
      // password: there is nothing to reset yet.
      return new Error(
        'This account still has its temporary password. Sign in with that, or ask an administrator to issue a new one.',
      );
    default:
      return error;
  }
}

function toSession(
  result: { IdToken?: string; AccessToken?: string; RefreshToken?: string; ExpiresIn?: number } | undefined,
  email: string,
): Session {
  if (!result?.IdToken || !result.AccessToken) {
    throw new Error('Sign-in did not return a token');
  }
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
    expiresAt: Date.now() + (result.ExpiresIn ?? 3600) * 1000,
    email,
  };
}
